import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from '../../shared/components/ui/buttons/gradient-btn/button';
import { Notice } from '../../shared/components/ui/notice/notice';
import { PasswordRecoveryService } from '../../core/services/password-recovery.service';
import { AppService } from '../../core/services/app.service';
import { AppStore, setDefaultSettingsTab } from '../../state/app.repository';

/**
 * Landing view for a password-recovery link (`/#recover=<token>`), shown as
 * `activeSection: 'recover-password'`. Confirms the target role and redeems
 * the one-time token only on Continue; on success the session is signed in
 * as that role and the view jumps to Settings → Permissions, where the
 * recovered role is pre-selected for a password change.
 */
@Component({
  selector: 'andor-recover-password',
  standalone: true,
  imports: [TranslatePipe, Button, Notice],
  templateUrl: './recover-password.html',
  styleUrl: './recover-password.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecoverPassword {
  readonly recovery = inject(PasswordRecoveryService);
  readonly #appService = inject(AppService);
  readonly #appStore = inject(AppStore);

  // Flattened for the template: `@switch` does not narrow the union.
  readonly status = computed(() => this.recovery.landing()?.status ?? 'inspecting');
  readonly roleName = computed(() => {
    const state = this.recovery.landing();
    return state && (state.status === 'ready' || state.status === 'consuming')
      ? state.roleName
      : '';
  });

  async continue(): Promise<void> {
    const role = await this.recovery.consume();
    if (!role) return;
    this.#appService.handlePermissionsUpdate(role);
    // Same deep-select pattern as `RoomLogin.openDevSettings()`. The
    // permissions tab only renders once `editAppPermissions` is true, which
    // `handlePermissionsUpdate` just applied.
    setDefaultSettingsTab('permissions');
    this.#appStore.update({ activeSection: 'general-settings', isConnectedView: false });
  }

  retry(): void {
    this.recovery.retry();
  }

  back(): void {
    this.recovery.dismiss();
    this.#appStore.update(this.#appService.preJoinViewState(false));
  }
}
